import React, { Fragment } from 'react'
import { string, object, bool, func, number } from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import classnames from 'classnames'
import {
  TYPES_WITH_FILTER,
  IS_EXPLORER,
  TYPES_WITH_ACTIONS,
  TYPES_WITH_INNER_SEARCH,
  TYPES_WITH_SORTING,
  TYPES_WITH_BREADCRUMBS,
  PROJECTS,
  IMAGES,
  EMPTY_OBJECT,
  FOLDERS,
  PROFESSIONALS,
  PRODUCTS,
  ARTICLE_ARCHIVE
} from '../../utils/constants'
import { showMetaDescriptionAndGetFilterTitle, objectToUrlPath } from '../../helpers/common'
import { useFiltersState } from '../../providers/filters/filtersContext'
import Aggregations from '../aggregations/Aggregations'
import { COUNTRY, STATES } from '../filters/constants'
import DynamicTitlesAndDescriptions from '../dynamic-title-description/DynamicTitlesAndDescriptions'
import Filters from '../filters/Filters'
import SortOptions from '../filters/SortOptions'
import Icon from '../ui/icon/Icon'
import FollowButton from '../ui/button/FollowButton'
import { filteredCategoriesPerType, dataTypeFollowButton } from '../ui/button/followButtonFns'
import { resultsPageMessages as messages } from './messages'
import { searchBarMessages } from '../messages'
import Insights from '../search-objects/Insights'
import SearchBarWithState from '../SearchBarWithState'
import ResultCount from './ResultCount'
import Breadcrumbs from '../ui/breadcrumbs/Breadcrumbs'

const SearchHeader = ({
  site,
  scope,
  aggregations = EMPTY_OBJECT,
  aggregationActive,
  totalCount,
  lastQueryUsed,
  gridView,
  filters,
  requestResults,
  setView,
  isGridAble,
  history,
  location,
  metadata,
  handleSearch
}) => {
  const intl = useIntl()
  const { activeCategories = EMPTY_OBJECT, filtersObject = EMPTY_OBJECT } = useFiltersState()
  const isFiltersView = TYPES_WITH_FILTER.includes(aggregationActive)
  const hasFilters = Object.keys(filters).length > 0
  const filtersWithActionsClass = classnames({
    'search-header__filter-with-actions': [PROJECTS, PRODUCTS].includes(aggregationActive) && !lastQueryUsed && hasFilters

  })

  const searchContainerClasses = classnames(filtersWithActionsClass, {
    'search-header--with-inner-search': TYPES_WITH_INNER_SEARCH.includes(aggregationActive),
    'search-header__row--sticky': !TYPES_WITH_INNER_SEARCH.includes(aggregationActive)
  })

  let showDynamicTitle = showMetaDescriptionAndGetFilterTitle(aggregationActive, filtersObject, filters, intl)
  let showMatchedResults = <FormattedMessage {...messages.matchedResults} />

  if (aggregationActive === PROFESSIONALS && filtersObject.hasOwnProperty(COUNTRY)) {
    const currentSearch = filtersObject.hasOwnProperty(STATES) ? STATES : COUNTRY
    const currentLabel = filters[currentSearch].find(element => element.slug === filtersObject[currentSearch])
    showDynamicTitle = `${showDynamicTitle} in ${currentLabel.name}`
  }

  if (lastQueryUsed) {
    const currentLabelFilter = <span>{` "${lastQueryUsed}" `}  </span>
    showMatchedResults = (
      <Fragment>
        <FormattedMessage {...messages.results} />
        {currentLabelFilter}
      </Fragment>
    )
  }

  const renderActions = (
    <div className={`search-header__actions ${filtersWithActionsClass}`}>
      <Insights category='search-layout-toggler' label='listview' value={aggregationActive}>
        <Icon onClick={() => setView(false)} svg='hamburger' isSelected={!gridView} />
      </Insights>
      <Insights category='search-layout-toggler' label='gridview' value={aggregationActive}>
        <Icon onClick={() => setView(true)} svg='gridview' isSelected={gridView} />
      </Insights>
    </div>
  )

  const filtersComponent = (
    <Filters
      searchType={aggregationActive}
      history={history}
      site={site}
      scope={scope}
      location={location}
      request={requestResults}
      data={filters}
    />
  )

  // Render filters with or without actions
  const renderFilters = isFiltersView && (
    <div className={`search-header__row search-header__row--with-filters ${searchContainerClasses}`}>
      {TYPES_WITH_INNER_SEARCH.includes(aggregationActive) &&
        <SearchBarWithState
          onSearch={handleSearch}
          query={lastQueryUsed}
          placeholder={searchBarMessages[aggregationActive]}
          aggregationActive={aggregationActive}
          clearQuery={() => handleSearch('')}
        />}
      <div className='search-header__actions-container'>
        {filtersComponent}
        {!lastQueryUsed && TYPES_WITH_ACTIONS.includes(aggregationActive) && renderActions}
        {TYPES_WITH_SORTING.includes(aggregationActive) && (lastQueryUsed.length > 0 || Object.keys(filtersObject).length > 0) &&
          <SortOptions
            requestResults={
              sortValue => requestResults(objectToUrlPath(filtersObject, ARTICLE_ARCHIVE), null, lastQueryUsed, sortValue)
            }
          />
        }
      </div>
    </div>
  )

  const renderResultsCount = (
    <h3 className='search-header__title'>
      {totalCount && `${totalCount.toLocaleString('en-US')} `}
      {showMatchedResults}
    </h3>
  )

  const renderAggregations = (
    <div className='search-header__row'>
      <Aggregations
        active={aggregationActive}
        aggregations={aggregations}
        query={lastQueryUsed}
        site={site}
        scope={scope}
      />
    </div>
  )

  const renderFollowButton = (filtersObject, type) => {
    if (!filtersObject?.categories || window.innerWidth < 1040) return null
    const { categories } = filtersObject
    const { name, slug } = filteredCategoriesPerType[type](activeCategories, categories, filters) || ''
    return <FollowButton
      type={dataTypeFollowButton(type)}
      slug={slug}
      label={name}
      entity={type}
      filtersObject={filtersObject}
    />
  }

  const renderDinamicTitlesAndDescriptions = (
    <DynamicTitlesAndDescriptions
      dynamicTitle={showDynamicTitle || metadata.landing_title}
      totalCount={totalCount}
      showMatchedResults={showMatchedResults}
      description={metadata.description}
      type={aggregationActive}
      renderFollowButton={renderFollowButton}
      filtersObject={filtersObject}
      aggregationActive={aggregationActive}
    />
  )

  return (
    <>
      <div className='search-header'>
        {TYPES_WITH_BREADCRUMBS.includes(aggregationActive) &&
          <Breadcrumbs searchType={aggregationActive} activeItem={filtersObject.topic} />}

        {/* Dynamic titles and Descriptions */}
        {site !== 'cn' && (!lastQueryUsed || aggregationActive === ARTICLE_ARCHIVE) &&
          aggregationActive !== FOLDERS && renderDinamicTitlesAndDescriptions}

        {/* Results numbers and actions section for Search All and mobile version */}
        {
          (lastQueryUsed || window.innerWidth < 1040) && aggregationActive !== PROFESSIONALS && (
            <div className='search-header__row'>
              {aggregationActive !== ARTICLE_ARCHIVE && renderResultsCount}
              {isGridAble &&
                !IS_EXPLORER &&
                TYPES_WITH_ACTIONS.includes(aggregationActive) &&
                renderActions
              }
            </div>
          )
        }

        {/* Results numbers and actions section for CN */}
        {site === 'cn' && !lastQueryUsed && (
          <div className='search-header__row'>
            {renderResultsCount}
            {isGridAble && !IS_EXPLORER && aggregationActive !== IMAGES && renderActions}
          </div>
        )}

        {/* Aggregations menu */}
        {Object.keys(aggregations).length > 1 && lastQueryUsed && renderAggregations}

        {/* Aggregations menu only for Projects and Images without query */}
        {
          (site === 'us' && Object.keys(aggregations).length > 1 && !lastQueryUsed) &&
          (aggregationActive === PROJECTS || aggregationActive === IMAGES) && renderAggregations
        }

      </div>
      {/* Filters */}
      {hasFilters && renderFilters}
      {TYPES_WITH_INNER_SEARCH.includes(aggregationActive) && (lastQueryUsed || Object.keys(filtersObject).length > 0) &&
        <ResultCount totalResults={totalCount} query={lastQueryUsed} filters={filtersObject} />
      }
    </>
  )
}

SearchHeader.propTypes = {
  aggregations: object,
  filters: object,
  staticData: object,
  history: object,
  location: object,
  query: string,
  site: string,
  scope: string,
  aggregationActive: string,
  lastQueryUsed: string,
  metadata: object,
  totalCount: number,
  gridView: bool,
  isGridAble: bool,
  requestResults: func,
  setView: func,
  handleSearch: func
}

export default SearchHeader
